<template>
    <f7-page
        class="chat-view"
        ref="self"
        :class="{'with-transition-subnavbar': withTransitionSubnavbar}"
    >
        <f7-navbar
            ref="navbar"
            class="chat-view__navbar"
            :class="{'with-subnavbar': (payment && BuyingOperationStatus.ONAPPEAL === payment!.status)}"
        >
            <PaymentChatViewNavbarContent
                ref="navbarContent"
                :chat-resource="chatResource"
                :payment="payment"
                @closePage="closePage"
                @subnavbarEventClick="subnavbarEventClick"
            />
        </f7-navbar>
        <f7-messagebar
            v-if="!chatInfoIsLoading"
            @messagebar:focus="messagebarFocus"
            @messagebar:blur="messagebarBlur"
            ref="messagebar"
            v-model:value="messageText"
            :placeholder="placeholder"
            :attachments-visible="attachmentsVisible"
            :sheet-visible="false"
        >
            <template #inner-start>
                <div class="attach-file-button-wrapper" @click.stop="tryUploadImage"
                     :class="{'disabled' : vc.attachments.value.length > 0}">
                    <i class="icon svg-clip"></i>
                </div>
                <input accept="image/*,application/pdf" type="file" ref="input-file" class="input-file"
                       @change="inputFileEventChange">
            </template>
            <template #inner-end>
                <f7-preloader color="#007AFF" class="preloader" v-if="sendMessageRequest"></f7-preloader>
                <i v-else class="send-icon icon i-svg-arrow-top"
                   :class="{'active': sendActive, 'disabled': !sendActive}"
                   @click="sendMessage" @touchstart="sendMessage"></i>
            </template>
            <f7-messagebar-attachments>
                <f7-messagebar-attachment
                    v-for="(image, index) in vc.filterAttachmentsByTypes()"
                    :key="index"
                    :image="image.path"
                    @attachment:delete="vc.removeAttachment"
                ></f7-messagebar-attachment>
            </f7-messagebar-attachments>
        </f7-messagebar>
        <TransitionGroup name="fade" mode="out-in">
            <template v-if="!chatInfoIsLoading && chatResource">
                <MessagesContent
                    :chat="chatResource"
                    @imageClick="openImage"
                />
            </template>
            <template v-else>
                <div>
                    <f7-preloader :size="28"></f7-preloader>
                </div>
            </template>
        </TransitionGroup>
    </f7-page>
</template>
<script lang="ts">
import {useI18n} from 'vue-i18n'
import {f7} from "framework7-vue";
import NoMessages from './components/NoMessages.vue';
import {ToRef, defineComponent, ref} from 'vue';
import dayjs from "dayjs";
import 'dayjs/locale/ru';
import isBetween from 'dayjs/plugin/isBetween'
import ChatDataController from '@/controllers/ChatDataController';
import Helper from "@/helpers/helper";
import ChatViewController from "@/controllers/chat-view/ChatViewController";
// @ts-ignore
import AppController from "@target/components/App/ts/AppController";
import PaymentService from "@/services/operations/payment/PaymentService";
import PaymentChatViewNavbarContent from "@/views/chat/components/PaymentChatViewNavbarContent.vue";
import {BuyingOperationStatus} from "@enums/BuyingOperationStatus";
import MessagesContent from "@/views/chat/components/MessagesContent.vue";
import {getDevice} from "framework7/shared/get-device";
import HuntingAgentsService from "@/services/others/HuntingAgentsService";
import LogService from "@/services/log-service/LogService";
import ServiceChat from "@/services/v2/data/service-chat/ServiceChat";
import ServiceTelegram from "@/services/v2/service-telegram/ServiceTelegram";

dayjs.extend(isBetween)

export default defineComponent({
    components: {
        MessagesContent,
        PaymentChatViewNavbarContent,
        NoMessages,
    },
    setup() {
        const {t} = useI18n({useScope: 'global'});
        const paymentService: PaymentService = AppController.getInstance().paymentService;
        const serviceTelegram: ServiceTelegram | null = AppController.getInstance().service<ServiceTelegram>('telegram');
        const {payment} = paymentService;
        const myName = "";
        const sendMessageRequest = ref(false)
        const messageText = ref('')
        const height = window.innerHeight
        const messagebar = null;
        const disableUploadFile = ref(false);
        const chatController = ChatDataController.getInstance();
        const vc = ChatViewController.getInstance();
        const attachments = vc.attachments;
        const chatResource = ServiceChat.of().chat;
        const loadingBtn = ref(false);
        const observer = new MutationObserver(() => {
            const pce: HTMLElement = document.querySelector('.chat-view')!.querySelector('.page-content')!;
            pce.scrollTo({top: pce.scrollHeight, behavior: 'smooth'});
        });
        const placeholder = t('chat.input-placeholder');
        const withTransitionSubnavbar: ToRef<boolean> = ref(false);

        return {
            withTransitionSubnavbar,
            paymentService,
            serviceTelegram,
            dayjs,
            placeholder,
            observer,
            attachments,
            vc,
            Helper,
            loadingBtn,
            chatController,
            chatResource,
            t,
            disableUploadFile,
            messagebar,
            height,
            messageText,
            myName,
            payment,
            sendMessageRequest
        }
    },
    data() {
        return {
            typingMessage: null,
            chatInfoIsLoading: false,
            messagesData: [],
            images: [],
            answers: [],
            responseInProgress: false,
        };
    },
    computed: {
        //TODO useless computed property
        BuyingOperationStatus() {
            return BuyingOperationStatus
        },
        sendActive() {
            return this.messageText.length > 0 || this.vc.attachments.value.length > 0;
        },
        attachmentsVisible() {
            return this.vc.filterAttachmentsByTypes().length > 0;
        }
    },
    watch: {
        'payment'(payment) {
            if (payment.status &&
                [
                    BuyingOperationStatus.REJECTED,
                ].includes(payment.status)) {
                this.showSubnavbar();
            }
        }
    },
    async created() {
        await this.fetchChatData();
        setTimeout(() => {
            if (this.checkShowSubnavbar()) this.showSubnavbar();
        }, 1000);
    },
    async mounted() {
        this.setPopupHeight('up');
        this.detectDeviceType();
    },
    unmounted() {
        this.observer.disconnect();
    },
    methods: {
        tryUploadImage() {
            LogService.of().log("tryUploadImage");
            if (getDevice().ios && getDevice().cordova) {
                try {
                    cordova.plugins.diagnostic.getCameraRollAuthorizationStatus((status: any) => {
                        switch (status) {
                            case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
                                cordova.plugins.diagnostic.requestCameraRollAuthorization(() => {
                                    switch (status) {
                                        default:
                                            cordova.plugins.diagnostic.getCameraAuthorizationStatus((status) => {
                                                switch (status) {
                                                    case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
                                                        cordova.plugins.diagnostic.requestCameraAuthorization((status) => {
                                                            (this.$refs as any)['input-file'].click()
                                                        });
                                                        break;
                                                    //case cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS:
                                                    //    cordova.plugins.diagnostic.switchToSettings();
                                                    //        break;
                                                    default:
                                                        (this.$refs as any)['input-file'].click()
                                                }
                                            });
                                    }
                                });
                                break;
                            //case cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS:
                            //cordova.plugins.diagnostic.switchToSettings();
                            //break;
                            default:
                                cordova.plugins.diagnostic.getCameraAuthorizationStatus((status) => {
                                    switch (status) {
                                        case cordova.plugins.diagnostic.permissionStatus.NOT_REQUESTED:
                                            cordova.plugins.diagnostic.requestCameraAuthorization((status) => {
                                                // @ts-ignore
                                                (this.$refs as any)['input-file'].click()
                                            });
                                            break;
                                        //case cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS:
                                        //    cordova.plugins.diagnostic.switchToSettings();
                                        //        break;
                                        default:
                                            // @ts-ignore
                                            (this.$refs as any)['input-file'].click()
                                    }
                                });
                        }
                    });
                } catch (e) {
                    console.log(e);
                }
                return;
            } else {
                (this.$refs as any)['input-file'].click()
            }
        },
        checkShowSubnavbar() {
            return !!(this.payment &&
                this.payment.status && [
                    BuyingOperationStatus.REJECTED,
                ].includes(this.payment.status));
        },
        showSubnavbar() {
            const navbarDOM = ((this.$refs.navbar as any).$el) as HTMLElement;
            navbarDOM.classList.add('with-custom-subnavbar');
            const pageContentDOM = (((this.$refs.self as any).$el) as HTMLElement).querySelector('.page-content') as HTMLElement;
            pageContentDOM.style.paddingTop = `88px`;
            (this.$refs.navbarContent as any).showSubnavbar = true;
        },
        hideSubnavbar() {
            const navbarDOM = ((this.$refs.navbar as any).$el) as HTMLElement;
            navbarDOM.classList.add('with-custom-subnavbar');
            const pageContentDOM = (((this.$refs.self as any).$el) as HTMLElement).querySelector('.page-content') as HTMLElement;
            pageContentDOM.style.paddingTop = `44px`;
            (this.$refs.navbarContent as any).showSubnavbar = false;
        },
        subnavbarEventClick() {
            const self = this;
            f7.dialog.create({
                title: this.$t("alerts.complain.title"),
                text: this.$t("alerts.complain.text"),
                cssClass: "dialog_complain",
                buttons: [
                    {
                        text: this.$t("alerts.complain.no")
                    },
                    {
                        text: this.$t("alerts.complain.yes"),
                        onClick: async () => {
                            await self.makeRequestToAppeal();
                            this.hideSubnavbar();
                        }
                    }
                ]
            }).open();
        },
        async makeRequestToAppeal() {
            try {
                f7.preloader.show();
                await this.paymentService.toAppealPayment();
            } catch (e: any) {
                f7.dialog.alert(this.t('g.errors.alert.default-text'), this.t('g.errors.alert.default-title'));
            } finally {
                f7.preloader.hide();
            }
        },
        inputFileEventChange(event: Event) {
            this.vc.uploadFile(event);
            if (this.vc.fileIsPDF(this.vc.attachments.value[0].file)) {
                this.sendMessage();
            }
            (event.target as HTMLInputElement).value = "";
        },
        openImage(path: string) {
            f7.photoBrowser.create({
                photos: [path],
                theme: 'dark'
            }).open();
        },
        setPopupHeight(arrow) {
            const popup = this.$refs['self'].$el.closest('.popup-router-open-in');
            if (arrow === 'down') {
                popup.style.height = '50%';
                popup.style.top = 'unset';
                popup.style.bottom = '0';
            } else {
                popup.style.height = '100%';
                popup.style.top = 'unset';
                popup.style.bottom = '0';
            }
        },
        detectDeviceType() {
            const standalone = window.navigator.standalone,
                userAgent = window.navigator.userAgent.toLowerCase(),
                safari = /safari/.test(userAgent),
                ios = /iphone|ipod|ipad/.test(userAgent);

            if (ios) {
                if (!standalone && safari) {
                    // Safari
                    // console.log('Safary');
                } else if (!standalone && !safari) {
                    // iOS webview
                    // console.log('IOS');
                }
            } else {
                if (userAgent.includes('wv')) {
                    // Android webview
                    // console.log('Android');
                } else {
                    // Chrome
                    // console.log('Chrome');
                }
            }
            return this;
        },
        async fetchChatData() {
            try {
                this.chatInfoIsLoading = true;
                const serverModelChat = await ServiceChat.of().fetchChatByMsid(this.payment!.msid!);
                const modelChatF7 = ServiceChat.of().modelChatF7Factory(serverModelChat);
                ServiceChat.of().chat.value = modelChatF7;
                ChatDataController.getInstance().resetPaymentChatCount(this.payment.id);
                this.myName = this.chatResource!.client.name;

                const pce: HTMLElement = document.querySelector('.chat-view')!.querySelector('.page-content')!;
                this.observer.observe(pce, {childList: true});
            } catch (e) {
                console.log(e)
                f7.dialog.alert(this.t("chat.alert.first"), this.t("chat.alert.second"), () => this.closePage());
            } finally {
                setTimeout(() => {
                    this.chatInfoIsLoading = false;
                }, 700);
            }
        },
        convertDatetime(dateString) {
            const date = new Date(dateString);
            const day = date.getDate();
            const dayString = day < 10 ? `0${day}` : day.toString();
            const month = date.getMonth();
            const monthString = month < 10 ? `0${month}` : month.toString();
            const year = date.getFullYear();
            const hour = date.getHours();
            const hourString = hour < 10 ? `0${hour}` : hour.toString();
            const minutes = date.getMinutes();
            const minutesString = minutes < 10 ? `0${minutes}` : minutes.toString();
            return `${dayString}.${monthString}.${year} ${hourString}:${minutesString}`;
        },
        async closePage() {
            await this.closeKeyboardFocusInput(async () => {
                try {
                    // f7.popup.get(this.$refs['self'].$el.closest('.popup-router-open-in')).close();
                    // @ts-ignore
                    f7.views.higher.router.back("/higher");
                    if (this.payment) HuntingAgentsService.of().checkShowToast(this.payment);
                } catch (e) {
                }
            });
        },
        async closeKeyboardFocusInput(callback: Function) {
            if (getDevice().cordova) {
              // @ts-ignore
              this.$refs.self.$el.querySelectorAll('input, textarea').forEach(element => {
                  element.blur();
              });
              setTimeout(async () => {
                  await callback();
              }, 314);
              return false;
            }
            callback();
        },
        messagebarBlur() {
            document.documentElement.classList.remove('device-with-keyboard');
            if (!getDevice().cordova && !this.serviceTelegram?.isTelegram) {
                const el = this.$refs['messagebar'].$el;
                el.style.paddingBottom = `${parseInt(getComputedStyle(document.documentElement).getPropertyValue('--f7-safe-area-bottom'))}px`;

                const fullHeight = document.documentElement.clientHeight;
                const currentHeight = window.innerHeight;

                document.body.style.height = `calc(100%)`;
                document.body.style.transform = `translateY(${0}px)`;
            }
            const pce = document.querySelector('.chat-view')!.querySelector('.page-content')!;
            pce.scrollTop = pce.scrollHeight;

            const el = this.$refs['messagebar'].$el;
            el.style.paddingBottom = 'calc(0px + var(--f7-safe-area-bottom))';
        },
        messagebarFocus() {
            document.documentElement.classList.add('device-with-keyboard');

            if (!getDevice().cordova && !this.serviceTelegram?.isTelegram) {
                setTimeout(() => {
                    const fullHeight2 = document.documentElement.clientHeight;
                    const currentHeight2 = window.innerHeight;
                    document.body.style.height = `${currentHeight2}px`;
                    document.body.style.transform = `translateY(${fullHeight2 - currentHeight2}px)`;
                }, 130);
            }
            setTimeout(() => {
                const pce = document.querySelector('.chat-view')!.querySelector('.page-content')!;
                pce.scrollTo({
                    top: pce.scrollHeight,
                    behavior: "smooth"
                });
                // }
            }, 401);
            const el = this.$refs['messagebar'].$el;
            el.style.paddingBottom = 'calc(0px)';
        },
        async sendMessage() {
            const text = this.messageText.replace(/\n/g, '<br>').trim();

            if (text.length === 0 && this.attachments.length === 0) return false;

            this.sendMessageRequest = true;

            try {
                const responseMessage = await this.vc.sendTextMessage({
                    msid: this.payment!.msid!,
                    message: text,
                    wait_files: this.attachments.length
                });

                if (this.attachments.length > 0) {
                    await Promise.all(
                        this.attachments.map(async (attachment) => {
                            const responseMessageFile = await this.vc.sendFileMessage({
                                chatId: this.chatResource!.id,
                                messageId: responseMessage.id,
                                file: attachment.file
                            });
                            responseMessage.attachments.push(responseMessageFile);
                        })
                    );
                }

                this.chatController.appendMessage(responseMessage);

                this.vc.removeAttachment();
                this.messageText = '';

                const pageContent = document.querySelector('.chat-view .page-content');
                if (pageContent) {
                    setTimeout(() => {
                        pageContent.scrollTo({
                            top: pageContent.scrollHeight,
                            behavior: 'smooth'
                        });
                    }, 600);
                }
            } catch (e: any) {
                if (typeof e.type !== 'undefined') {
                    switch (e.type) {
                        case 'ServerAuthError':
                            this.closePage();
                    }
                }
            } finally {
                this.sendMessageRequest = false;
            }
        },
    },
});
</script>

<style lang="scss">


.chat-view {
    --f7-subnavbar-height: 32px;
    --f7-page-content-extra-padding-bottom: 0px;

    .toolbar {
        .toolbar-inner {
            .messagebar-attachment {
                img {
                    object-fit: contain;
                    max-height: 155px;
                    max-width: 100%;

                    @media (min-width: 426px) {
                        max-height: 120px !important;
                    }
                }
            }

            .attach-file-button-wrapper {
                z-index: 99999;
                display: flex;
                align-self: flex-end;
                cursor: pointer;
                margin-right: 4px;
                margin-bottom: 8px;

                .svg-clip {
                }
            }

            .input-file {
                display: none;
            }

            .send-icon {
                align-self: flex-end;
                margin-bottom: 5px;
            }

            .preloader {
                align-self: flex-end;
                margin-bottom: 10px;
            }
        }
    }


    .page-content {
        background-color: #FFF;
        transform: translate3d(0, 0, 0);
        will-change: transform;
        transition: 0.5s padding-top cubic-bezier(0.250, 0.460, 0.450, 0.940);

        &::-webkit-scrollbar {
            display: none;
        }

        .preloader {
            margin-top: 44px;
            margin-left: 50%;
            transform: translateX(-50%);
        }

        .fade-enter-active,
        .fade-leave-active {
            transition: opacity 0.5s ease;
        }

        .fade-enter-from,
        .fade-leave-to {
            opacity: 0;
        }
    }

    .messages-block {
        .appeal-title {
            margin: 16px;
            text-align: center;
            @include text(12px, 500, #818E95, 14.4px);
        }

        &::-webkit-scrollbar {
            display: none;
        }

        .no-messages {
            padding-top: 56px;
        }

        .messages-title:first-child {
            margin-top: 16px;
        }

        .message + .messages-title {
            margin-top: 24px;
        }

        .message {
            cursor: pointer;

            .avatar {
                width: 29px;
                height: 29px;
                background-size: contain;

                &.agent {
                    background-image: url("data:image/svg+xml,%3Csvg width='33' height='33' viewBox='0 0 33 33' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='33' height='33' rx='16.5' fill='%23EFEFF4'/%3E%3Cpath d='M7.51214 13.3248L7.43861 13.28C6.43076 14.9349 5.89443 16.8256 5.88607 18.7542V18.7546C5.88607 20.9878 6.953 22.8622 8.96186 24.1714C10.8692 25.4151 13.5689 26.1012 16.5631 26.099C19.5569 26.0967 22.2568 25.4146 24.1642 24.1714C26.173 22.8624 27.2404 20.9882 27.2399 18.7546C27.2399 16.2121 26.2631 13.6637 24.5598 11.7607C23.1811 10.22 20.6744 8.38502 16.5631 8.3818L16.5629 8.3818C14.4695 8.38505 12.791 8.86135 11.4632 9.53328L11.4632 9.53317L11.4592 9.53545L11.4439 9.54426C9.80751 10.3767 8.42617 11.6636 7.44871 13.2656L7.44859 13.2658L7.44841 13.266C7.44672 13.2681 7.44186 13.2743 7.43735 13.2822L7.51214 13.3248ZM7.51214 13.3248C7.51341 13.3226 7.51498 13.3206 7.51657 13.3186C7.51784 13.317 7.51912 13.3154 7.52027 13.3136L9.00886 24.0992C7.02216 22.8045 5.97217 20.9562 5.97217 18.7546C5.98046 16.8417 6.5124 14.9663 7.51214 13.3248Z' fill='white' stroke='%23423636' stroke-width='0.172195'/%3E%3Cpath d='M16.5852 7.48745H16.5851L16.5395 7.48745L16.5393 7.48745C14.5071 7.49314 12.8344 7.9149 11.4734 8.53179C10.7471 7.52634 9.65923 6.8912 8.58497 6.8912C7.68263 6.8912 6.77621 7.35657 6.09433 8.15677L6.09431 8.15679C5.45428 8.90843 5.08472 9.85129 5.08472 10.7457C5.08472 11.7045 5.57818 12.6091 6.41186 13.248C5.47749 14.9384 4.98368 16.8299 4.97494 18.754V18.7544C4.97494 21.3015 6.18234 23.4337 8.4576 24.9171L8.50462 24.8449L8.45761 24.9171C10.5077 26.2532 13.378 26.9882 16.5399 26.9937H16.54H16.5856H16.5857C19.7479 26.9879 22.6177 26.2532 24.668 24.9171L24.621 24.8449L24.668 24.9171C26.9432 23.4338 28.1511 21.3017 28.1511 18.7544L28.1511 18.754C28.1422 16.8365 27.6517 14.9512 26.7233 13.2652C27.5736 12.6271 28.0738 11.7135 28.0738 10.7457C28.0738 9.85129 27.7042 8.90843 27.0642 8.15679L26.9986 8.21261L27.0642 8.15679C26.3827 7.35657 25.4764 6.8912 24.5737 6.8912C23.4953 6.8912 22.4038 7.53124 21.6762 8.54279C20.3106 7.91969 18.6299 7.49142 16.5852 7.48745ZM9.05578 24.0273L9.05576 24.0273C7.09126 22.747 6.05821 20.9248 6.05815 18.7549C6.06641 16.8586 6.59357 14.9993 7.58445 13.3716L7.5865 13.369C7.58799 13.367 7.59007 13.3642 7.5923 13.3608L7.59233 13.3608L7.59368 13.3586C8.55541 11.7811 9.91461 10.5151 11.5238 9.69692L11.5238 9.69702L11.5276 9.69483L11.5429 9.68606C12.8465 9.02677 14.4978 8.55738 16.563 8.55416C20.6157 8.55737 23.0783 10.3637 24.4314 11.8757L24.4955 11.8183L24.4314 11.8757C26.1066 13.7473 27.0676 16.2549 27.0676 18.7548C27.0681 20.925 26.0346 22.7472 24.0701 24.0273L24.1171 24.0994L24.0701 24.0273C22.1982 25.2473 19.5338 25.9247 16.5629 25.927C13.5919 25.9292 10.9276 25.2478 9.05578 24.0273ZM7.51645 13.3188C7.51772 13.3172 7.51901 13.3156 7.52016 13.3138L5.97206 18.7547C5.98035 16.8419 6.51229 14.9665 7.51203 13.325C7.5133 13.3228 7.51486 13.3208 7.51645 13.3188Z' fill='%23423636' stroke='%23423636' stroke-width='0.172195'/%3E%3Cpath d='M18.1693 18.6436C18.1693 19.1697 16.6535 19.9026 16.6535 19.9026C16.6535 19.9026 15.1377 19.1697 15.1377 18.6436C15.1377 18.1176 15.8167 18.0546 16.6535 18.0546C17.4904 18.0546 18.1693 18.1177 18.1693 18.6436Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M15.5132 18.5756C15.5043 18.5848 15.493 18.5973 15.493 18.6435C15.493 18.6405 15.493 18.6389 15.4933 18.6388C15.4952 18.6384 15.5102 18.6949 15.6264 18.8116C15.7435 18.9291 15.9098 19.0546 16.0908 19.1735C16.2691 19.2907 16.4493 19.3935 16.5859 19.4675C16.61 19.4805 16.6327 19.4926 16.6537 19.5037C16.6746 19.4926 16.6973 19.4805 16.7214 19.4675C16.858 19.3935 17.0382 19.2907 17.2165 19.1735C17.3975 19.0546 17.5639 18.9291 17.681 18.8116C17.7972 18.6949 17.8121 18.6384 17.814 18.6388C17.8143 18.6389 17.8143 18.6405 17.8143 18.6435C17.8143 18.5973 17.803 18.5848 17.7942 18.5756C17.7761 18.5569 17.7296 18.5235 17.6242 18.4922C17.4063 18.4274 17.077 18.4096 16.6537 18.4096C16.2303 18.4096 15.9011 18.4274 15.6831 18.4922C15.5777 18.5235 15.5312 18.5568 15.5132 18.5756ZM16.6537 19.9025L16.4989 20.2222L16.4972 20.2214L16.4933 20.2195L16.4791 20.2125C16.4669 20.2064 16.4495 20.1978 16.4276 20.1866C16.3838 20.1644 16.3218 20.1322 16.2477 20.0921C16.1002 20.0122 15.9013 19.8989 15.7007 19.7671C15.5027 19.637 15.2902 19.4806 15.1231 19.3128C14.9739 19.163 14.7827 18.9285 14.7827 18.6435C14.7827 18.4268 14.8563 18.2341 15.0009 18.0836C15.1363 17.9426 15.3119 17.8615 15.4808 17.8113C15.8115 17.713 16.2402 17.6993 16.6537 17.6993C17.0672 17.6993 17.4958 17.713 17.8266 17.8113C17.9955 17.8615 18.171 17.9426 18.3064 18.0836C18.4511 18.2341 18.5246 18.4268 18.5246 18.6435C18.5246 18.9285 18.3334 19.163 18.1843 19.3128C18.0172 19.4806 17.8046 19.637 17.6066 19.7671C17.406 19.8989 17.2072 20.0122 17.0596 20.0921C16.9855 20.1322 16.9235 20.1644 16.8797 20.1866C16.8578 20.1978 16.8404 20.2064 16.8283 20.2125L16.8141 20.2195L16.8101 20.2214L16.8089 20.222L16.6537 19.9025ZM16.6537 19.9025L16.8089 20.222C16.7112 20.2692 16.5966 20.2695 16.4989 20.2222L16.6537 19.9025Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.6559 19.4133C16.9286 19.4133 17.1497 19.6343 17.1497 19.9071V20.5332C17.1497 20.6371 17.1169 20.7384 17.0559 20.8226L16.6559 20.5332C17.0559 20.8226 17.0558 20.8229 17.0556 20.8231L17.0553 20.8236L17.0545 20.8247L17.0525 20.8274L17.047 20.8348L17.0298 20.8572C17.0157 20.8753 16.9961 20.8997 16.9711 20.9293C16.9212 20.9883 16.8494 21.0682 16.7561 21.1594C16.5704 21.3409 16.2947 21.5718 15.9328 21.7701C15.1952 22.174 14.1197 22.4284 12.7861 21.9013C12.5324 21.8011 12.4081 21.5142 12.5083 21.2606C12.6086 21.0069 12.8954 20.8826 13.1491 20.9828C14.1723 21.3873 14.9409 21.1873 15.4583 20.9039C15.7238 20.7585 15.9283 20.5875 16.0659 20.4531C16.1033 20.4165 16.1354 20.383 16.1621 20.3539V19.9071C16.1621 19.6343 16.3831 19.4133 16.6559 19.4133Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.6515 19.4133C16.9242 19.4133 17.1453 19.6343 17.1453 19.9071V20.3539C17.172 20.383 17.2041 20.4165 17.2415 20.4531C17.379 20.5875 17.5835 20.7585 17.849 20.9039C18.3664 21.1873 19.135 21.3873 20.1583 20.9828C20.4119 20.8826 20.6988 21.0069 20.799 21.2606C20.8993 21.5142 20.7749 21.8011 20.5213 21.9013C19.1877 22.4284 18.1121 22.174 17.3746 21.7701C17.0126 21.5718 16.737 21.3409 16.5512 21.1594C16.4579 21.0682 16.3861 20.9883 16.3362 20.9293C16.3113 20.8997 16.2917 20.8753 16.2775 20.8572L16.2604 20.8348L16.2549 20.8274L16.2529 20.8247L16.2521 20.8236L16.2517 20.8231C16.2516 20.8229 16.2514 20.8226 16.6515 20.5332L16.2514 20.8226C16.1905 20.7384 16.1577 20.6371 16.1577 20.5332V19.9071C16.1577 19.6343 16.3788 19.4133 16.6515 19.4133Z' fill='%23423636'/%3E%3Cpath d='M24.5778 13.4455C24.5643 13.323 24.4998 13.2114 24.3996 13.1369C24.2994 13.0627 24.1724 13.0321 24.0487 13.0529L17.1648 14.205H16.1299L9.24562 13.0529C9.12187 13.0321 8.99487 13.0627 8.8947 13.1369C8.79469 13.2114 8.73013 13.323 8.71663 13.4455C8.70574 13.5474 8.45531 15.9535 9.68094 17.4393C10.2169 18.0891 10.9449 18.4639 11.8455 18.5529C12.0503 18.5736 12.2558 18.5843 12.4617 18.5847C13.5239 18.5847 14.4118 18.2868 15.1061 17.6964C15.5597 17.3022 15.9161 16.8118 16.1483 16.2626H17.1465C17.3787 16.8119 17.7348 17.3022 18.1882 17.6964C18.883 18.2868 19.7704 18.5847 20.8331 18.5847H20.8333C21.039 18.5842 21.2445 18.5736 21.4493 18.5529C22.3495 18.4634 23.078 18.089 23.6138 17.4393C24.8396 15.9537 24.5892 13.5476 24.5778 13.4455Z' fill='%23423636'/%3E%3C/svg%3E%0A");
                }

                &.admin {
                    background-image: url("data:image/svg+xml,%3Csvg width='33' height='33' viewBox='0 0 33 33' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='33' height='33' rx='16.5' fill='%23EFEFF4'/%3E%3Cpath d='M7.51214 13.3248L7.43861 13.28C6.43076 14.9349 5.89443 16.8256 5.88607 18.7542V18.7546C5.88607 20.9878 6.953 22.8622 8.96186 24.1714C10.8692 25.4151 13.5689 26.1012 16.5631 26.099C19.5569 26.0967 22.2568 25.4146 24.1642 24.1714C26.173 22.8624 27.2404 20.9882 27.2399 18.7546C27.2399 16.2121 26.2631 13.6637 24.5598 11.7607C23.1811 10.22 20.6744 8.38502 16.5631 8.3818L16.5629 8.3818C14.4695 8.38505 12.791 8.86135 11.4632 9.53328L11.4632 9.53317L11.4592 9.53545L11.4439 9.54426C9.80751 10.3767 8.42617 11.6636 7.44871 13.2656L7.44859 13.2658L7.44841 13.266C7.44672 13.2681 7.44186 13.2743 7.43735 13.2822L7.51214 13.3248ZM7.51214 13.3248C7.51341 13.3226 7.51498 13.3206 7.51657 13.3186C7.51784 13.317 7.51912 13.3154 7.52027 13.3136L9.00886 24.0992C7.02216 22.8045 5.97217 20.9562 5.97217 18.7546C5.98046 16.8417 6.5124 14.9663 7.51214 13.3248Z' fill='white' stroke='%23423636' stroke-width='0.172195'/%3E%3Cpath d='M16.5852 7.48745H16.5851L16.5395 7.48745L16.5393 7.48745C14.5071 7.49314 12.8344 7.9149 11.4734 8.53179C10.7471 7.52634 9.65923 6.8912 8.58497 6.8912C7.68263 6.8912 6.77621 7.35657 6.09433 8.15677L6.09431 8.15679C5.45428 8.90843 5.08472 9.85129 5.08472 10.7457C5.08472 11.7045 5.57818 12.6091 6.41186 13.248C5.47749 14.9384 4.98368 16.8299 4.97494 18.754V18.7544C4.97494 21.3015 6.18234 23.4337 8.4576 24.9171L8.50462 24.8449L8.45761 24.9171C10.5077 26.2532 13.378 26.9882 16.5399 26.9937H16.54H16.5856H16.5857C19.7479 26.9879 22.6177 26.2532 24.668 24.9171L24.621 24.8449L24.668 24.9171C26.9432 23.4338 28.1511 21.3017 28.1511 18.7544L28.1511 18.754C28.1422 16.8365 27.6517 14.9512 26.7233 13.2652C27.5736 12.6271 28.0738 11.7135 28.0738 10.7457C28.0738 9.85129 27.7042 8.90843 27.0642 8.15679L26.9986 8.21261L27.0642 8.15679C26.3827 7.35657 25.4764 6.8912 24.5737 6.8912C23.4953 6.8912 22.4038 7.53124 21.6762 8.54279C20.3106 7.91969 18.6299 7.49142 16.5852 7.48745ZM9.05578 24.0273L9.05576 24.0273C7.09126 22.747 6.05821 20.9248 6.05815 18.7549C6.06641 16.8586 6.59357 14.9993 7.58445 13.3716L7.5865 13.369C7.58799 13.367 7.59007 13.3642 7.5923 13.3608L7.59233 13.3608L7.59368 13.3586C8.55541 11.7811 9.91461 10.5151 11.5238 9.69692L11.5238 9.69702L11.5276 9.69483L11.5429 9.68606C12.8465 9.02677 14.4978 8.55738 16.563 8.55416C20.6157 8.55737 23.0783 10.3637 24.4314 11.8757L24.4955 11.8183L24.4314 11.8757C26.1066 13.7473 27.0676 16.2549 27.0676 18.7548C27.0681 20.925 26.0346 22.7472 24.0701 24.0273L24.1171 24.0994L24.0701 24.0273C22.1982 25.2473 19.5338 25.9247 16.5629 25.927C13.5919 25.9292 10.9276 25.2478 9.05578 24.0273ZM7.51645 13.3188C7.51772 13.3172 7.51901 13.3156 7.52016 13.3138L5.97206 18.7547C5.98035 16.8419 6.51229 14.9665 7.51203 13.325C7.5133 13.3228 7.51486 13.3208 7.51645 13.3188Z' fill='%23423636' stroke='%23423636' stroke-width='0.172195'/%3E%3Cpath d='M18.1693 18.6436C18.1693 19.1697 16.6535 19.9026 16.6535 19.9026C16.6535 19.9026 15.1377 19.1697 15.1377 18.6436C15.1377 18.1176 15.8167 18.0546 16.6535 18.0546C17.4904 18.0546 18.1693 18.1177 18.1693 18.6436Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M15.5132 18.5756C15.5043 18.5848 15.493 18.5973 15.493 18.6435C15.493 18.6405 15.493 18.6389 15.4933 18.6388C15.4952 18.6384 15.5102 18.6949 15.6264 18.8116C15.7435 18.9291 15.9098 19.0546 16.0908 19.1735C16.2691 19.2907 16.4493 19.3935 16.5859 19.4675C16.61 19.4805 16.6327 19.4926 16.6537 19.5037C16.6746 19.4926 16.6973 19.4805 16.7214 19.4675C16.858 19.3935 17.0382 19.2907 17.2165 19.1735C17.3975 19.0546 17.5639 18.9291 17.681 18.8116C17.7972 18.6949 17.8121 18.6384 17.814 18.6388C17.8143 18.6389 17.8143 18.6405 17.8143 18.6435C17.8143 18.5973 17.803 18.5848 17.7942 18.5756C17.7761 18.5569 17.7296 18.5235 17.6242 18.4922C17.4063 18.4274 17.077 18.4096 16.6537 18.4096C16.2303 18.4096 15.9011 18.4274 15.6831 18.4922C15.5777 18.5235 15.5312 18.5568 15.5132 18.5756ZM16.6537 19.9025L16.4989 20.2222L16.4972 20.2214L16.4933 20.2195L16.4791 20.2125C16.4669 20.2064 16.4495 20.1978 16.4276 20.1866C16.3838 20.1644 16.3218 20.1322 16.2477 20.0921C16.1002 20.0122 15.9013 19.8989 15.7007 19.7671C15.5027 19.637 15.2902 19.4806 15.1231 19.3128C14.9739 19.163 14.7827 18.9285 14.7827 18.6435C14.7827 18.4268 14.8563 18.2341 15.0009 18.0836C15.1363 17.9426 15.3119 17.8615 15.4808 17.8113C15.8115 17.713 16.2402 17.6993 16.6537 17.6993C17.0672 17.6993 17.4958 17.713 17.8266 17.8113C17.9955 17.8615 18.171 17.9426 18.3064 18.0836C18.4511 18.2341 18.5246 18.4268 18.5246 18.6435C18.5246 18.9285 18.3334 19.163 18.1843 19.3128C18.0172 19.4806 17.8046 19.637 17.6066 19.7671C17.406 19.8989 17.2072 20.0122 17.0596 20.0921C16.9855 20.1322 16.9235 20.1644 16.8797 20.1866C16.8578 20.1978 16.8404 20.2064 16.8283 20.2125L16.8141 20.2195L16.8101 20.2214L16.8089 20.222L16.6537 19.9025ZM16.6537 19.9025L16.8089 20.222C16.7112 20.2692 16.5966 20.2695 16.4989 20.2222L16.6537 19.9025Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.6559 19.4133C16.9286 19.4133 17.1497 19.6343 17.1497 19.9071V20.5332C17.1497 20.6371 17.1169 20.7384 17.0559 20.8226L16.6559 20.5332C17.0559 20.8226 17.0558 20.8229 17.0556 20.8231L17.0553 20.8236L17.0545 20.8247L17.0525 20.8274L17.047 20.8348L17.0298 20.8572C17.0157 20.8753 16.9961 20.8997 16.9711 20.9293C16.9212 20.9883 16.8494 21.0682 16.7561 21.1594C16.5704 21.3409 16.2947 21.5718 15.9328 21.7701C15.1952 22.174 14.1197 22.4284 12.7861 21.9013C12.5324 21.8011 12.4081 21.5142 12.5083 21.2606C12.6086 21.0069 12.8954 20.8826 13.1491 20.9828C14.1723 21.3873 14.9409 21.1873 15.4583 20.9039C15.7238 20.7585 15.9283 20.5875 16.0659 20.4531C16.1033 20.4165 16.1354 20.383 16.1621 20.3539V19.9071C16.1621 19.6343 16.3831 19.4133 16.6559 19.4133Z' fill='%23423636'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M16.6515 19.4133C16.9242 19.4133 17.1453 19.6343 17.1453 19.9071V20.3539C17.172 20.383 17.2041 20.4165 17.2415 20.4531C17.379 20.5875 17.5835 20.7585 17.849 20.9039C18.3664 21.1873 19.135 21.3873 20.1583 20.9828C20.4119 20.8826 20.6988 21.0069 20.799 21.2606C20.8993 21.5142 20.7749 21.8011 20.5213 21.9013C19.1877 22.4284 18.1121 22.174 17.3746 21.7701C17.0126 21.5718 16.737 21.3409 16.5512 21.1594C16.4579 21.0682 16.3861 20.9883 16.3362 20.9293C16.3113 20.8997 16.2917 20.8753 16.2775 20.8572L16.2604 20.8348L16.2549 20.8274L16.2529 20.8247L16.2521 20.8236L16.2517 20.8231C16.2516 20.8229 16.2514 20.8226 16.6515 20.5332L16.2514 20.8226C16.1905 20.7384 16.1577 20.6371 16.1577 20.5332V19.9071C16.1577 19.6343 16.3788 19.4133 16.6515 19.4133Z' fill='%23423636'/%3E%3Cpath d='M24.5778 13.4455C24.5643 13.323 24.4998 13.2114 24.3996 13.1369C24.2994 13.0627 24.1724 13.0321 24.0487 13.0529L17.1648 14.205H16.1299L9.24562 13.0529C9.12187 13.0321 8.99487 13.0627 8.8947 13.1369C8.79469 13.2114 8.73013 13.323 8.71663 13.4455C8.70574 13.5474 8.45531 15.9535 9.68094 17.4393C10.2169 18.0891 10.9449 18.4639 11.8455 18.5529C12.0503 18.5736 12.2558 18.5843 12.4617 18.5847C13.5239 18.5847 14.4118 18.2868 15.1061 17.6964C15.5597 17.3022 15.9161 16.8118 16.1483 16.2626H17.1465C17.3787 16.8119 17.7348 17.3022 18.1882 17.6964C18.883 18.2868 19.7704 18.5847 20.8331 18.5847H20.8333C21.039 18.5842 21.2445 18.5736 21.4493 18.5529C22.3495 18.4634 23.078 18.089 23.6138 17.4393C24.8396 15.9537 24.5892 13.5476 24.5778 13.4455Z' fill='%23007AFF'/%3E%3C/svg%3E%0A");
                }
            }

            .message-bubble {
                max-width: 264px;

                .message-image {
                    display: flex;
                    justify-content: center;

                    &:has(.chat-message-pdf) {
                        margin-right: -16px;
                        margin-bottom: 0;
                    }

                    img {
                        height: 256px;
                        width: 256px;
                        object-fit: cover;
                    }
                }

                .message-text {
                    // & > span {
                    // display: inline-flex;
                    // flex-direction: column;
                    .read-status {
                        margin-left: 6px;
                        float: right;

                        .check-svg {
                            width: 16px;
                            height: 16px;
                            background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.6622 4.23019C13.3518 3.91634 12.846 3.91238 12.5308 4.22137L6.61746 10.0657L4.04688 7.40454C3.74029 7.085 3.23271 7.07451 2.9132 7.3811C2.59365 7.68769 2.58316 8.19527 2.88976 8.51478C2.89115 8.5162 2.8925 8.51763 2.89389 8.51905L6.02817 11.7672C6.17816 11.9202 6.38317 12.0068 6.59746 12.0077H6.60628C6.81649 12.0071 7.01807 11.9238 7.16754 11.776L13.6549 5.36154C13.9684 5.05071 13.9716 4.54501 13.6622 4.23019Z' fill='white'/%3E%3C/svg%3E%0A");

                            &.was-read {
                                background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_3219_105256)'%3E%3Cpath d='M10.9956 4.23019C10.6853 3.91634 10.1795 3.91238 9.8643 4.22137L3.95096 10.0657L1.38037 7.40454C1.07378 7.085 0.566209 7.07451 0.246696 7.3811C-0.072851 7.68769 -0.0833393 8.19527 0.223254 8.51478C0.224643 8.5162 0.225997 8.51763 0.227386 8.51905L3.36166 11.7672C3.51166 11.9202 3.71667 12.0068 3.93095 12.0077H3.93977C4.14999 12.0071 4.35156 11.9238 4.50104 11.776L10.9884 5.36154C11.3019 5.05071 11.3051 4.54501 10.9956 4.23019Z' fill='white'/%3E%3Cpath d='M8.1724 11.7672C8.32201 11.9199 8.52636 12.0064 8.74009 12.0078H8.74891C8.95913 12.0071 9.1607 11.9239 9.31018 11.776L15.7976 5.36159C16.0917 5.03048 16.0616 4.52371 15.7305 4.22965C15.4304 3.96307 14.9793 3.95956 14.6751 4.22142L8.76412 10.0658L8.55324 9.84689C8.24669 9.52734 7.73911 9.51682 7.41957 9.82341C7.10002 10.13 7.0895 10.6375 7.39609 10.9571C7.39748 10.9585 7.39887 10.96 7.40026 10.9614L8.1724 11.7672Z' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_3219_105256'%3E%3Crect width='16' height='16' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
                            }
                        }
                    }

                    // }
                }
            }
        }

        .message-received {
            .message-image {
                //margin-left: -16px;
                //padding: 4px;
                //img {
                //    border-bottom-right-radius: var(--f7-message-bubble-border-radius) !important;
                //    border-bottom-left-radius: var(--f7-message-bubble-border-radius) !important;
                //}
            }
        }

        .message-sent {
            .message-image {
                //margin-right: -16px;
                //padding: 4px;
                //img {
                //    border-bottom-right-radius: var(--f7-message-bubble-border-radius) !important;
                //    border-bottom-left-radius: var(--f7-message-bubble-border-radius) !important;
                //}
            }
        }
    }

    .preloader {
        width: 34px;
        height: 24px;
        margin-left: 8px;
    }

    .i-svg-arrow-top {
        cursor: pointer;
        margin-left: 8px;
        width: 34px;
        height: 34px;
        background-image: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 14C0 6.26801 6.26801 0 14 0V0C21.732 0 28 6.26801 28 14V14C28 21.732 21.732 28 14 28V28C6.26801 28 0 21.732 0 14V14Z' fill='%23818E95'/%3E%3Cpath d='M13.9962 21C14.5607 21 14.9498 20.6252 14.9498 20.0667V11.358L14.8811 9.76325L16.6891 11.7328L18.4132 13.379C18.5887 13.5407 18.8175 13.6509 19.0922 13.6509C19.6109 13.6509 20 13.2835 20 12.769C20 12.5265 19.9008 12.2987 19.7025 12.1076L14.7057 7.28661C14.5226 7.10289 14.2556 7 13.9962 7C13.7368 7 13.4774 7.10289 13.2943 7.28661L8.29752 12.1076C8.09917 12.2987 8 12.5265 8 12.769C8 13.2835 8.38907 13.6509 8.90782 13.6509C9.18245 13.6509 9.41132 13.5407 9.58678 13.379L11.3032 11.7328L13.1113 9.76325L13.0502 11.358V20.0667C13.0502 20.6252 13.4317 21 13.9962 21Z' fill='white'/%3E%3C/svg%3E%0A");

        &.active {
            background-image: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 14C0 6.26801 6.26801 0 14 0V0C21.732 0 28 6.26801 28 14V14C28 21.732 21.732 28 14 28V28C6.26801 28 0 21.732 0 14V14Z' fill='%23007AFF'/%3E%3Cpath d='M13.9962 21C14.5607 21 14.9498 20.6252 14.9498 20.0667V11.358L14.8811 9.76325L16.6891 11.7328L18.4132 13.379C18.5887 13.5407 18.8175 13.6509 19.0922 13.6509C19.6109 13.6509 20 13.2835 20 12.769C20 12.5265 19.9008 12.2987 19.7025 12.1076L14.7057 7.28661C14.5226 7.10289 14.2556 7 13.9962 7C13.7368 7 13.4774 7.10289 13.2943 7.28661L8.29752 12.1076C8.09917 12.2987 8 12.5265 8 12.769C8 13.2835 8.38907 13.6509 8.90782 13.6509C9.18245 13.6509 9.41132 13.5407 9.58678 13.379L11.3032 11.7328L13.1113 9.76325L13.0502 11.358V20.0667C13.0502 20.6252 13.4317 21 13.9962 21Z' fill='white'/%3E%3C/svg%3E%0A");
        }
    }
}

.i-svg-icon-close {
    width: 24px;
    height: 24px;
    filter: brightness(0) saturate(100%) invert(26%) sepia(93%) saturate(2553%) hue-rotate(203deg) brightness(107%) contrast(105%);
    background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M13.6159 12.0001L19.6649 5.95111C20.1117 5.50426 20.1117 4.78199 19.6649 4.33514C19.218 3.88829 18.4958 3.88829 18.0489 4.33514L12 10.3842L5.95107 4.33514C5.50423 3.88829 4.78197 3.88829 4.33513 4.33514C3.88829 4.78199 3.88829 5.50426 4.33513 5.95111L10.3841 12.0001L4.33513 18.0492C3.88829 18.496 3.88829 19.2183 4.33513 19.6651C4.55798 19.888 4.85054 20 5.1431 20C5.43566 20 5.72822 19.888 5.95107 19.6651L12 13.6161L18.0489 19.6651C18.2718 19.888 18.5643 20 18.8569 20C19.1495 20 19.442 19.888 19.6649 19.6651C20.1117 19.2183 20.1117 18.496 19.6649 18.0492L13.6159 12.0001Z' fill='white'/%3E%3C/svg%3E%0A");
}
</style>
